import { ConfigDatabase } from '../../engine/database';
import { asteroidsData, type AsteroidConfig } from '../../generated/asteroids';
import { asteroidsSizeData, type AsteroidSizeConfig } from '../../generated/asteroidsSize';
import { asteroidMixData, type AsteroidMixConfig } from '../../generated/asteroidMix';
import {
  asteroidMixEntriesData,
  type AsteroidMixEntryConfig,
} from '../../generated/asteroidMixEntries';

export type AsteroidId = string;
export type AsteroidSizeId = string;
export type AsteroidMixId = string;

/**
 * Интерфейс для астероида с дополнительной информацией
 */
export interface AsteroidInfo extends AsteroidConfig {
  sizeInfo: AsteroidSizeConfig;
  isUnlocked: boolean;
  spawnWeight: number;
  difficulty: number;
}

/**
 * Интерфейс для размера астероида с дополнительной информацией
 */
export interface AsteroidSizeInfo extends AsteroidSizeConfig {
  asteroids: AsteroidInfo[];
  totalWeight: number;
}

/**
 * Интерфейс для микса астероидов с дополнительной информацией
 */
export interface AsteroidMixInfo extends AsteroidMixConfig {
  entries: Array<AsteroidMixEntryConfig & { asteroidInfo: AsteroidInfo }>;
  totalWeight: number;
}

/**
 * База данных для астероидов
 * Объединяет типы астероидов, размеры и миксы
 */
export class GameAsteroidsDB {
  private asteroidsDB: ConfigDatabase<AsteroidConfig, string>;
  private asteroidsSizeDB: ConfigDatabase<AsteroidSizeConfig, string>;
  private asteroidMixDB: ConfigDatabase<AsteroidMixConfig, string>;
  private asteroidMixEntriesDB: ConfigDatabase<AsteroidMixEntryConfig, string>;

  constructor() {
    this.asteroidsDB = new ConfigDatabase({
      base: asteroidsData,
      overrides: {},
      getId: config => config.asteroid_id,
      validate: (config, id) => {
        if (config.base_hp_unit <= 0) {
          throw new Error(`Asteroid ${id}: base_hp_unit must be > 0`);
        }
        if (config.tier < 1) {
          throw new Error(`Asteroid ${id}: tier must be >= 1`);
        }
      },
    });

    this.asteroidsSizeDB = new ConfigDatabase({
      base: asteroidsSizeData,
      overrides: {},
      getId: config => config.size_id,
      validate: (config, id) => {
        if (config.size_hp_mult <= 0) {
          throw new Error(`AsteroidSize ${id}: size_hp_mult must be > 0`);
        }
        if (config.size_value_mult <= 0) {
          throw new Error(`AsteroidSize ${id}: size_value_mult must be > 0`);
        }
      },
    });

    this.asteroidMixDB = new ConfigDatabase({
      base: asteroidMixData,
      overrides: {},
      getId: config => config.asteroid_mix_id,
      validate: () => {
        // Пока без валидации, так как нет числовых полей
      },
    });

    // Создаем Record из массива asteroidMixEntriesData
    const asteroidMixEntriesRecord: Record<string, AsteroidMixEntryConfig> = {};
    asteroidMixEntriesData.forEach(entry => {
      const id = `${entry.asteroid_mix_id}-${entry.asteroid_id}-${entry.size_id}`;
      asteroidMixEntriesRecord[id] = entry;
    });

    this.asteroidMixEntriesDB = new ConfigDatabase({
      base: asteroidMixEntriesRecord,
      overrides: {},
      getId: config =>
        `${config.asteroid_mix_id}-${config.asteroid_id}-${config.size_id}` as string,
      validate: (config, id) => {
        if (config.weight < 0) {
          throw new Error(`AsteroidMixEntry ${id}: weight must be >= 0`);
        }
      },
    });
  }

  /**
   * Инициализация всех внутренних баз данных
   */
  init(): void {
    this.asteroidsDB.init();
    this.asteroidsSizeDB.init();
    this.asteroidMixDB.init();
    this.asteroidMixEntriesDB.init();
  }

  // ===== ASTEROIDS =====

  /**
   * Получить информацию об астероиде
   */
  getAsteroidInfo(asteroidId: AsteroidId): AsteroidInfo | undefined {
    const asteroid = this.asteroidsDB.get(asteroidId);
    if (!asteroid) return undefined;

    // Получаем первый доступный размер для астероида из микс-записей
    const firstEntry = this.asteroidMixEntriesDB
      .all()
      .find(entry => entry.asteroid_id === asteroidId);

    if (!firstEntry) return undefined;

    const sizeInfo = this.asteroidsSizeDB.get(firstEntry.size_id);
    if (!sizeInfo) return undefined;

    // TODO: Интегрировать с системой прогресса игрока
    const isUnlocked = asteroid.tier <= 3; // Пока разблокированы астероиды до 3 тира

    // Вычисляем вес спауна (чем выше тир, тем реже)
    const spawnWeight = 1.0 / asteroid.tier;

    // Вычисляем сложность на основе HP и тира
    const difficulty = asteroid.base_hp_unit * sizeInfo.size_hp_mult * asteroid.tier;

    return {
      ...asteroid,
      sizeInfo,
      isUnlocked,
      spawnWeight,
      difficulty,
    };
  }

  /**
   * Получить все астероиды
   */
  getAllAsteroids(): AsteroidInfo[] {
    return this.asteroidsDB
      .all()
      .map(asteroid => this.getAsteroidInfo(asteroid.asteroid_id)!)
      .filter(asteroid => asteroid !== undefined);
  }

  /**
   * Получить астероиды по тиру
   */
  getAsteroidsByTier(tier: number): AsteroidInfo[] {
    return this.asteroidsDB
      .all()
      .filter(asteroid => asteroid.tier === tier)
      .map(asteroid => this.getAsteroidInfo(asteroid.asteroid_id)!)
      .filter(asteroid => asteroid !== undefined);
  }

  /**
   * Получить астероиды по размеру
   */
  getAsteroidsBySize(sizeId: AsteroidSizeId): AsteroidInfo[] {
    // Получаем астероиды через микс-записи
    const asteroidIds = this.asteroidMixEntriesDB
      .all()
      .filter(entry => entry.size_id === sizeId)
      .map(entry => entry.asteroid_id);

    return asteroidIds
      .map(asteroidId => this.getAsteroidInfo(asteroidId)!)
      .filter(asteroid => asteroid !== undefined);
  }

  /**
   * Получить разблокированные астероиды
   */
  getUnlockedAsteroids(): AsteroidInfo[] {
    return this.getAllAsteroids().filter(asteroid => asteroid.isUnlocked);
  }

  /**
   * Получить астероиды для спауна (с весами)
   */
  getAsteroidsForSpawn(): Array<{ asteroid: AsteroidInfo; weight: number }> {
    return this.getUnlockedAsteroids().map(asteroid => ({
      asteroid,
      weight: asteroid.spawnWeight,
    }));
  }

  // ===== ASTEROID SIZES =====

  /**
   * Получить информацию о размере астероида
   */
  getAsteroidSizeInfo(sizeId: AsteroidSizeId): AsteroidSizeInfo | undefined {
    const size = this.asteroidsSizeDB.get(sizeId);
    if (!size) return undefined;

    const asteroids = this.getAsteroidsBySize(sizeId);
    const totalWeight = asteroids.reduce((sum, asteroid) => sum + asteroid.spawnWeight, 0);

    return {
      ...size,
      asteroids,
      totalWeight,
    };
  }

  /**
   * Получить все размеры астероидов
   */
  getAllAsteroidSizes(): AsteroidSizeInfo[] {
    return this.asteroidsSizeDB
      .all()
      .map(size => this.getAsteroidSizeInfo(size.size_id)!)
      .filter(size => size !== undefined);
  }

  /**
   * Получить размеры по порядку (от маленького к большому)
   */
  getAsteroidSizesInOrder(): AsteroidSizeInfo[] {
    return this.getAllAsteroidSizes().sort((a, b) => a.size_hp_mult - b.size_hp_mult);
  }

  // ===== ASTEROID MIXES =====

  /**
   * Получить информацию о миксе астероидов
   */
  getAsteroidMixInfo(mixId: AsteroidMixId): AsteroidMixInfo | undefined {
    const mix = this.asteroidMixDB.get(mixId);
    if (!mix) return undefined;

    const entries = this.asteroidMixEntriesDB
      .all()
      .filter(entry => entry.asteroid_mix_id === mixId)
      .map(entry => {
        const asteroidInfo = this.getAsteroidInfo(entry.asteroid_id);
        return {
          ...entry,
          asteroidInfo: asteroidInfo!,
        };
      })
      .filter(entry => entry.asteroidInfo !== undefined);

    const totalWeight = entries.reduce((sum, entry) => sum + entry.weight, 0);

    return {
      ...mix,
      entries,
      totalWeight,
    };
  }

  /**
   * Получить все миксы астероидов
   */
  getAllAsteroidMixes(): AsteroidMixInfo[] {
    return this.asteroidMixDB
      .all()
      .map(mix => this.getAsteroidMixInfo(mix.asteroid_mix_id)!)
      .filter(mix => mix !== undefined);
  }

  /**
   * Получить миксы по миру
   */
  getAsteroidMixesByWorld(_world: number): AsteroidMixInfo[] {
    // Пока нет поля world в AsteroidMixConfig, возвращаем все миксы
    return this.getAllAsteroidMixes();
  }

  /**
   * Получить микс по ID
   */
  getAsteroidMixById(mixId: AsteroidMixId): AsteroidMixInfo | undefined {
    return this.getAsteroidMixInfo(mixId);
  }

  // ===== SIZE & VISUAL HELPERS =====

  /**
   * Получить размер астероида по sizeId
   */
  sizeIdToAsteroidSize(sizeId: AsteroidSizeId): string {
    const mapping: Record<string, string> = {
      SZ_PEB: 'pebble',
      SZ_S: 'small',
      SZ_M: 'medium',
      SZ_L: 'large',
      SZ_C: 'colossal',
    };
    return mapping[sizeId] || 'small';
  }

  /**
   * Получить размер в пикселях по sizeId
   */
  getSizePx(sizeId: AsteroidSizeId): number {
    // Маппинг размеров на пиксели (временно хардкод)
    const sizeMapping: Record<string, number> = {
      SZ_PEB: 64, // pebble
      SZ_S: 96, // small
      SZ_M: 128, // medium
      SZ_L: 256, // large
      SZ_C: 384, // colossal
    };
    return sizeMapping[sizeId] ?? 96; // fallback to small size
  }

  /**
   * Получить цвет по тиру
   */
  getTintByTier(tier: number): number {
    const tintByTier: Record<number, number> = {
      1: 0x10b981,
      2: 0x3b82f6,
      3: 0x8b5cf6,
      4: 0xdc2626,
    };
    return tintByTier[tier] ?? tintByTier[1];
  }

  /**
   * Получить размер по тиру (fallback)
   */
  getSizeByTier(tier: number): string {
    const sizeByTier: Record<number, string> = {
      1: 'small',
      2: 'medium',
      3: 'large',
      4: 'colossal',
    };
    return sizeByTier[tier] ?? 'small';
  }

  // ===== FRAGMENTATION HELPERS =====

  /**
   * Получить информацию о дочернем размере для фрагментации
   */
  getChildSizeInfo(sizeId: AsteroidSizeId): AsteroidSizeInfo | undefined {
    const size = this.asteroidsSizeDB.get(sizeId);
    if (!size || size.child_size === '–' || size.frag_count <= 0) return undefined;

    return this.getAsteroidSizeInfo(size.child_size);
  }

  /**
   * Получить астероиды для фрагментации определенного размера
   */
  getAsteroidsForFragmentation(
    sizeId: AsteroidSizeId
  ): Array<{ asteroid: AsteroidInfo; weight: number }> {
    const childSizeInfo = this.getChildSizeInfo(sizeId);
    if (!childSizeInfo) return [];

    return childSizeInfo.asteroids.map(asteroid => ({
      asteroid,
      weight: asteroid.spawnWeight,
    }));
  }

  /**
   * Получить случайный астероид для фрагментации с учетом весов
   */
  getRandomAsteroidForFragmentation(sizeId: AsteroidSizeId): AsteroidInfo | null {
    const candidates = this.getAsteroidsForFragmentation(sizeId);
    if (!candidates.length) return null;

    const totalWeight = candidates.reduce((sum, item) => sum + item.weight, 0);
    if (totalWeight <= 0) return candidates[0]?.asteroid || null;

    let random = Math.random() * totalWeight;
    for (const { asteroid, weight } of candidates) {
      random -= weight;
      if (random <= 0) return asteroid;
    }

    return candidates[candidates.length - 1]?.asteroid || null;
  }

  // ===== UTILITY METHODS =====

  /**
   * Получить все ID астероидов
   */
  getAllAsteroidIds(): AsteroidId[] {
    return this.asteroidsDB.ids() as AsteroidId[];
  }

  /**
   * Получить все ID размеров
   */
  getAllAsteroidSizeIds(): AsteroidSizeId[] {
    return this.asteroidsSizeDB.ids() as AsteroidSizeId[];
  }

  /**
   * Получить все ID миксов
   */
  getAllAsteroidMixIds(): AsteroidMixId[] {
    return this.asteroidMixDB.ids() as AsteroidMixId[];
  }

  /**
   * Проверить существование астероида
   */
  hasAsteroid(asteroidId: AsteroidId): boolean {
    return this.asteroidsDB.has(asteroidId);
  }

  /**
   * Проверить существование размера
   */
  hasAsteroidSize(sizeId: AsteroidSizeId): boolean {
    return this.asteroidsSizeDB.has(sizeId);
  }

  /**
   * Проверить существование микса
   */
  hasAsteroidMix(mixId: AsteroidMixId): boolean {
    return this.asteroidMixDB.has(mixId);
  }

  /**
   * Получить астероиды по сложности
   */
  getAsteroidsByDifficulty(minDifficulty: number, maxDifficulty: number): AsteroidInfo[] {
    return this.getAllAsteroids().filter(
      asteroid => asteroid.difficulty >= minDifficulty && asteroid.difficulty <= maxDifficulty
    );
  }

  /**
   * Получить самый сложный астероид
   */
  getHardestAsteroid(): AsteroidInfo | undefined {
    const asteroids = this.getAllAsteroids();
    return asteroids.reduce((hardest, current) =>
      current.difficulty > hardest.difficulty ? current : hardest
    );
  }

  /**
   * Получить самый легкий астероид
   */
  getEasiestAsteroid(): AsteroidInfo | undefined {
    const asteroids = this.getAllAsteroids();
    return asteroids.reduce((easiest, current) =>
      current.difficulty < easiest.difficulty ? current : easiest
    );
  }

  /**
   * Получить статистику по астероидам
   */
  getAsteroidStats(): {
    totalAsteroids: number;
    unlockedAsteroids: number;
    totalSizes: number;
    totalMixes: number;
    averageDifficulty: number;
  } {
    const asteroids = this.getAllAsteroids();
    const unlockedAsteroids = this.getUnlockedAsteroids();
    const sizes = this.getAllAsteroidSizes();
    const mixes = this.getAllAsteroidMixes();

    const averageDifficulty =
      asteroids.length > 0
        ? asteroids.reduce((sum, asteroid) => sum + asteroid.difficulty, 0) / asteroids.length
        : 0;

    return {
      totalAsteroids: asteroids.length,
      unlockedAsteroids: unlockedAsteroids.length,
      totalSizes: sizes.length,
      totalMixes: mixes.length,
      averageDifficulty,
    };
  }
}

// Ленивый синглтон для удобства
let _gameAsteroidsInstance: GameAsteroidsDB | null = null;
export function getGameAsteroids(): GameAsteroidsDB {
  if (!_gameAsteroidsInstance) {
    _gameAsteroidsInstance = new GameAsteroidsDB();
  }
  return _gameAsteroidsInstance;
}

export const GameAsteroids = new Proxy({} as GameAsteroidsDB, {
  get(_target, prop) {
    const instance = getGameAsteroids();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const method = instance[prop];

    if (typeof method === 'function') {
      return method.bind(instance);
    }
    return method;
  },
});

// Реэкспортируем типы для удобства
export type { AsteroidConfig, AsteroidSizeConfig, AsteroidMixConfig, AsteroidMixEntryConfig };
